import { APP_INITIALIZER, Provider } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { CookieService } from 'ngx-cookie-service';

/**
 * TP2020-11188 — runs once, before the router/guards, on every app load.
 *
 * 1) Manager hand-off: if the admin app opened us with a `#h=` URL fragment, decode
 *    the management Cognito session it carries and write it into THIS app's own
 *    host-only cookie jar, then mark this tab as a manager session. Cookies are
 *    isolated per-subdomain now, so the session can no longer be shared ambiently —
 *    the fragment is the explicit, short-lived channel. (app/src/main.ts has already
 *    configured the management Amplify pool when it saw `#h=`.)
 *
 * 2) Sentinel reconcile: amoactkn is a synchronous guard marker set as a *session*
 *    cookie, while the Cognito tokens persist ~30 days. Re-align the two once at boot
 *    so a browser restart neither bounces a still-valid session to /login nor admits
 *    a tokenless one.
 */

function base64UrlDecode(input: string): string {
    const padded = input.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = atob(padded + '==='.slice((padded.length + 3) % 4));
    // Restore any multi-byte UTF-8 (token values are ASCII JWTs, but be safe).
    return decodeURIComponent(
        decoded
            .split('')
            .map((ch) => '%' + ('00' + ch.charCodeAt(0).toString(16)).slice(-2))
            .join('')
    );
}

function stripHandoffFromUrl(): void {
    if (typeof window === 'undefined' || !window.history?.replaceState) return;
    const cleanHash = (window.location.hash || '')
        .replace(/[#&]h=[^&]*/, '')
        .replace(/^&/, '#')
        .replace(/[#&]$/, '');
    const url = window.location.pathname + window.location.search + (cleanHash && cleanHash !== '#' ? cleanHash : '');
    window.history.replaceState(window.history.state, '', url);
}

function consumeHandoff(cookie: CookieService): void {
    if (typeof window === 'undefined') return;
    const match = (window.location.hash || '').match(/[#&]h=([^&]+)/);
    if (!match) return;

    try {
        const body = JSON.parse(base64UrlDecode(decodeURIComponent(match[1])));
        const valid = body && body.v === 1 && body.c && typeof body.exp === 'number' && Date.now() <= body.exp;
        if (!valid) return;

        const host = window.location.hostname;
        const secure = window.location.protocol === 'https:';

        // Write the management Cognito session into this app's host-only jar. The keys
        // (CognitoIdentityServiceProvider.<MGMT_CLIENT>.*) match what Amplify's management
        // pool reads, so fetchAuthSession() resolves a real management session here.
        Object.keys(body.c).forEach((name) => {
            cookie.set(name, body.c[name], 30, '/', host, secure, 'Lax');
        });

        // App-side role + auth markers (host-only). amo_app_pool makes subsequent loads
        // boot straight into the management pool (see app/src/main.ts).
        cookie.set('amo_app_pool', 'management', 30, '/', host, secure, 'Lax');
        cookie.set('mgmt_token', 'amo_management', 0, '/', host, secure, 'Lax');
        cookie.set('amoactkn', '1', 0, '/', host, secure, 'Lax');
    } catch (e) {
        // Malformed/expired payload — ignore and fall through to the reconcile.
    } finally {
        stripHandoffFromUrl();
    }
}

async function reconcileSentinel(cookie: CookieService): Promise<void> {
    if (typeof window === 'undefined') return;
    const host = window.location.hostname;
    const secure = window.location.protocol === 'https:';

    let hasToken = false;
    try {
        const session = await fetchAuthSession();
        hasToken = !!session?.tokens?.accessToken;
    } catch (e) {
        hasToken = false;
    }

    const hasSentinel = cookie.check('amoactkn');
    if (hasToken && !hasSentinel) {
        cookie.set('amoactkn', '1', 0, '/', host, secure, 'Lax');
    } else if (!hasToken && hasSentinel) {
        cookie.delete('amoactkn', '/', host);
    }
}

export function sessionBootstrapFactory(cookie: CookieService): () => Promise<void> {
    return async (): Promise<void> => {
        consumeHandoff(cookie);
        await reconcileSentinel(cookie);
    };
}

export const SESSION_BOOTSTRAP_PROVIDER: Provider = {
    provide: APP_INITIALIZER,
    useFactory: sessionBootstrapFactory,
    deps: [CookieService],
    multi: true,
};
